import { signal } from '@preact/signals';
import { supabase } from '@/lib/supabase';

/** Web-push subscription (works in installed iOS PWAs, 16.4+). */

export type PushState = 'unknown' | 'unsupported' | 'denied' | 'subscribed' | 'unsubscribed' | 'failed';
export const pushState = signal<PushState>('unknown');

const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function urlB64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function initPushState() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC) {
    pushState.value = 'unsupported';
    return;
  }
  if (Notification.permission === 'denied') {
    pushState.value = 'denied';
    return;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    pushState.value = sub ? 'subscribed' : 'unsubscribed';
  } catch {
    pushState.value = 'unsubscribed';
  }
}

export async function subscribePush(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC) {
    pushState.value = 'unsupported';
    return 'unsupported';
  }
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
      pushState.value = 'denied';
      return 'denied';
    }
    const reg = await navigator.serviceWorker.ready;
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(VAPID_PUBLIC),
      }));
    const json = sub.toJSON();
    const { error } = await supabase.from('push_subscriptions').upsert(
      {
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
        ua: navigator.userAgent.slice(0, 200),
      },
      { onConflict: 'endpoint' }
    );
    if (error) throw new Error(error.message);
    pushState.value = 'subscribed';
    return 'subscribed';
  } catch (e) {
    console.warn('push subscribe failed', e);
    pushState.value = 'failed';
    return 'failed';
  }
}
